import { useLayoutEffect } from "react";

import type { Destructor } from "~/tools/destructor";
import { type OnResizeOptions, onResize } from "~/tools/on-resize";
import { useLatest } from "~/tools/use-latest";

export interface UseOnResizeOptions<TNode> extends Omit<OnResizeOptions<TNode>, "node"> {
	node: null | undefined | TNode;

	/**
	 * @default true
	 */
	enabled?: boolean;
}

export function useOnResize<TNode extends Element>(
	handler: (node: TNode, entry: ResizeObserverEntry) => void | Destructor,
	{ node, enabled = true, debounce }: UseOnResizeOptions<TNode>,
): void {
	const stableHandler = useLatest(handler);
	const enabledNode = enabled ? node : null;

	useLayoutEffect(() => {
		if (enabledNode == null) {
			return;
		}

		return onResize((...args) => stableHandler()(...args), { node: enabledNode, debounce });
	}, [enabledNode, stableHandler, debounce]);
}
